import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Receipt, ArrowLeft, CheckCircle, FileDown } from 'lucide-react';
import { exportListPDF } from '@/utils/pdfExport';
import { formatCurrency, fmtNum } from '@/utils/currency';

export default function SettledExpenses() {
  const { token } = useContext(AuthContext);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSettled();
  }, []);

  const fetchSettled = async () => {
    try {
      const response = await axios.get(`${API}/expenses/settled`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExpenses(response.data);
    } catch (error) {
      console.error('Failed to fetch settled expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = () => {
    const rows = expenses.map((exp, i) => [
      i + 1,
      new Date(exp.date).toLocaleDateString(),
      exp.expense_id || '-',
      exp.category_name || '-',
      exp.details || '-',
      `$${fmtNum(exp.amount || 0)}`,
      exp.settled_date ? new Date(exp.settled_date).toLocaleDateString() : '-',
    ]);
    const total = expenses.reduce((s, e) => s + (e.amount || 0), 0);
    exportListPDF({
      title: 'Settled Expenses',
      headers: ['#', 'Date', 'Expense ID', 'Category', 'Details', 'Amount', 'Settled Date'],
      rows,
      totalLabel: 'Total Settled',
      totalValue: `$${fmtNum(total)}`,
      token, apiUrl: API,
    });
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="settled-expenses-page">
        <div className="flex items-center gap-4">
          <a href="/expenses">
            <Button variant="outline" size="sm" data-testid="back-to-expenses">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Expenses
            </Button>
          </a>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900">Settled Expenses</h1>
            <p className="text-sm text-slate-500">{expenses.length} expense(s) settled</p>
          </div>
          <Button variant="outline" onClick={handleExportPDF} data-testid="settled-export-pdf">
            <FileDown className="w-4 h-4 mr-2" /> PDF
          </Button>
        </div>

        {loading ? (
          <div className="grid gap-4">
            {[1, 2, 3].map(i => (
              <Card key={i} className="p-6 animate-pulse">
                <div className="h-16 bg-slate-200 rounded" />
              </Card>
            ))}
          </div>
        ) : expenses.length === 0 ? (
          <Card className="p-12 text-center">
            <CheckCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 text-lg">No settled expenses</p>
            <p className="text-slate-500 text-sm mt-2">Settled expenses will appear here after you settle them from the Expenses page</p>
          </Card>
        ) : (
          <div className="grid gap-4">
            {expenses.map((expense) => (
              <Card key={expense.id} className="p-6 border-green-200 bg-green-50/30" data-testid={`settled-expense-${expense.expense_id}`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <Receipt className="w-5 h-5 text-green-600" />
                      <h3 className="text-xl font-bold text-slate-900">{expense.expense_id}</h3>
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-semibold">
                        SETTLED
                      </span>
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold">
                        {expense.category_name}
                      </span>
                    </div>
                    <p className="text-slate-700 mt-2">{expense.details}</p>
                    <p className="text-sm text-slate-500 mt-2">
                      Date: {new Date(expense.date).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right min-w-[180px]">
                    <p className="text-3xl font-bold text-green-700">{formatCurrency(expense.amount, expense.currency)}</p>
                    <div className="text-sm text-green-600 space-y-1 mt-2">
                      {expense.settled_date && <p>Settled: {new Date(expense.settled_date).toLocaleDateString()}</p>}
                      {expense.paid_from_bank && <p>Bank: {expense.paid_from_bank}</p>}
                      {expense.payee && <p>Payee: {expense.payee}</p>}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
